<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const grievances = [
  {
    id: 'GRV-2025-004',
    project: 'Mangrove Restoration Initiative – Borneo',
    submittedBy: 'Community Representative – Kalimantan',
    date: '2025-02-10',
    category: 'Stakeholder Consultation',
    summary: 'Concerns raised that Zone 3 community was not adequately consulted during the FPIC process.',
    status: 'Under Investigation',
    statusColor: 'yellow',
    dueDate: '2025-04-10',
  },
  {
    id: 'GRV-2024-019',
    project: 'Solar Park Beta – Northern Highlands',
    submittedBy: 'Local NGO – Rift Valley Watch',
    date: '2024-08-05',
    category: 'Environmental Impact',
    summary: 'Query regarding bird migration impact study completeness.',
    status: 'Resolved',
    statusColor: 'green',
    dueDate: '2024-10-05',
  },
]

const statusClass = (color: string) => {
  const map: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-600',
  }
  return map[color] ?? 'bg-gray-100 text-gray-600'
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Grievances</span>
        </nav>

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-accent mb-2">Grievance Management</h1>
                <p class="text-gray-600">
                  Article 6.4 requires a stakeholder grievance mechanism. View and respond to grievances filed against your projects, or submit a grievance of your own.
                </p>
              </div>
              <button class="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 flex items-center">
                <i class="fas fa-plus mr-2"></i>Submit Grievance
              </button>
            </div>
          </div>
        </section>

        <div class="grid grid-cols-3 gap-4 mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-accent">2</p>
            <p class="text-sm text-gray-600">Total Grievances</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-yellow-600">1</p>
            <p class="text-sm text-gray-600">Under Investigation</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-green-600">1</p>
            <p class="text-sm text-gray-600">Resolved</p>
          </div>
        </div>

        <!-- Grievance list -->
        <section class="space-y-4 mb-10">
          <div v-for="g in grievances" :key="g.id" class="bg-white rounded-lg border border-gray-200 p-5">
            <div class="flex items-start justify-between mb-3">
              <div>
                <div class="flex items-center space-x-3 mb-1">
                  <h3 class="font-medium text-accent">{{ g.project }}</h3>
                  <span :class="['inline-flex px-2 py-0.5 rounded-full text-xs font-medium', statusClass(g.statusColor)]">{{ g.status }}</span>
                </div>
                <p class="text-xs text-gray-500">Ref: {{ g.id }} &nbsp;·&nbsp; Category: {{ g.category }} &nbsp;·&nbsp; Submitted: {{ g.date }}</p>
              </div>
              <div class="text-right text-sm text-gray-500">
                <p>Response due</p>
                <p class="font-medium text-gray-700">{{ g.dueDate }}</p>
              </div>
            </div>
            <div class="bg-gray-50 rounded-md px-4 py-3 text-sm text-gray-700 mb-3">
              <p class="text-xs text-gray-500 mb-1">Submitted by: {{ g.submittedBy }}</p>
              {{ g.summary }}
            </div>
            <div class="flex items-center space-x-3">
              <button class="px-4 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primary/90">
                <i class="fas fa-reply mr-1"></i>Respond
              </button>
              <button class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                <i class="fas fa-eye mr-1"></i>View Full Record
              </button>
            </div>
          </div>
        </section>

        <!-- Submit form -->
        <section class="bg-white rounded-lg border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-accent mb-4">
            <i class="fas fa-plus-circle text-primary mr-2"></i>Submit New Grievance
          </h2>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Related Project</label>
              <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                <option>Select project</option>
                <option>Solar Park Beta</option>
                <option>Mangrove Restoration Initiative</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                <option>Select category</option>
                <option>Stakeholder Consultation</option>
                <option>Environmental Impact</option>
                <option>Community Rights</option>
                <option>Registry Process</option>
                <option>Other</option>
              </select>
            </div>
            <div class="lg:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-2">Description *</label>
              <textarea rows="4" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Describe the grievance in detail."></textarea>
            </div>
          </div>
          <div class="mt-4 flex justify-end">
            <button type="submit" class="px-6 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90">
              <i class="fas fa-paper-plane mr-2"></i>Submit Grievance
            </button>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
